import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import {
  useCreatePollMutation,
  useGetEngageAudiencesQuery,
} from "../../api/engageApi";
import AudienceDialog from "./AudienceDialog";
import { POLL_LIMITS, findAudience, normalizePoll } from "./constants";

const CreatePoll = ({ onBack, onPosted }) => {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [durationDays, setDurationDays] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [audienceId, setAudienceId] = useState("");
  const [audienceOpen, setAudienceOpen] = useState(false);

  const { data: audiences } = useGetEngageAudiencesQuery();
  const [createPoll, { isLoading: posting }] = useCreatePollMutation();

  const audience = findAudience(audiences, audienceId);

  const filled = useMemo(
    () => options.map((option) => option.trim()).filter(Boolean),
    [options]
  );

  const duplicate = useMemo(() => {
    const seen = new Set();
    return filled.some((option) => {
      const key = option.toLowerCase();
      if (seen.has(key)) return true;
      seen.add(key);
      return false;
    });
  }, [filled]);

  const days = Number(durationDays);
  const daysValid =
    durationDays !== "" &&
    Number.isInteger(days) &&
    days >= POLL_LIMITS.MIN_DAYS &&
    days <= POLL_LIMITS.MAX_DAYS;

  const canSubmit =
    Boolean(question.trim()) &&
    filled.length >= POLL_LIMITS.MIN_OPTIONS &&
    !duplicate &&
    daysValid &&
    Boolean(audience) &&
    !posting;

  const setOptionAt = (index, value) => {
    setOptions((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const addOption = () => {
    if (options.length >= POLL_LIMITS.MAX_OPTIONS) {
      toast.info(`A poll can have at most ${POLL_LIMITS.MAX_OPTIONS} options`);
      return;
    }
    setOptions((prev) => prev.concat([""]));
  };

  const removeOption = (index) => {
    setOptions((prev) => prev.filter((item, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      const created = await createPoll({
        question: question.trim(),
        options: filled,
        durationDays: days,
        anonymous,
        audience: {
          audienceType: audience.audienceType,
          audienceId: audience.id,
        },
      }).unwrap();

      const normalized = normalizePoll(created);
      if (normalized && normalized.poll) normalized.poll.anonymous = anonymous;

      toast.success("Poll posted successfully");
      if (onPosted) onPosted(normalized);
      onBack();
    } catch (error) {
      toast.error(error?.data?.message || "Could not post the poll");
    }
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* action bar */}
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Close"
            className="rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100"
          >
            &#10005;
          </button>
          <h2 className="flex-1 text-lg font-semibold text-gray-800">
            Create Poll
          </h2>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleCreate}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {posting && <CircularProgress size={14} color="inherit" />}
            Create
          </button>
        </div>

        {/* audience */}
        <div className="border-b border-gray-200 bg-blue-secondery-2 px-5 py-4">
          <p className="mb-1 block text-sm font-semibold text-gray-700">
            Visible to <span className="text-red-500">*</span>
          </p>
          <p className="mb-2 text-xs text-gray-500">
            Only people in this group can see this poll or vote on it.
          </p>
          <button
            type="button"
            onClick={() => setAudienceOpen(true)}
            className="flex w-full max-w-md items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:border-blue-500"
          >
            <span className="flex-1">
              {audience
                ? `${audience.sectionLabel}: ${audience.name}`
                : "Select a group"}
            </span>
            <span className="text-gray-400">&rsaquo;</span>
          </button>
        </div>

        {/* question */}
        <div className="border-b border-gray-200 px-5 py-4">
          <label
            htmlFor="poll-question"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Question <span className="text-red-500">*</span>
          </label>
          <input
            id="poll-question"
            value={question}
            maxLength={POLL_LIMITS.QUESTION_MAX}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Enter your question here"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        {/* options */}
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="mb-1 flex items-center">
            <p className="flex-1 text-sm font-semibold text-gray-700">
              Options <span className="text-red-500">*</span>
            </p>
            <button
              type="button"
              onClick={addOption}
              aria-label="Add option"
              className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-gray-200 bg-blue-secondery-1 text-lg font-semibold text-blue-700 hover:bg-blue-600 hover:text-white"
            >
              +
            </button>
          </div>
          <p className="mb-3 text-xs text-gray-400">
            {POLL_LIMITS.MIN_OPTIONS} minimum, {POLL_LIMITS.MAX_OPTIONS}{" "}
            maximum.
          </p>

          {options.map((option, index) => (
            <div
              key={`option-${index}`}
              className="mb-2 flex items-center gap-2"
            >
              <input
                value={option}
                onChange={(event) => setOptionAt(index, event.target.value)}
                placeholder={`Option ${index + 1}`}
                aria-label={`Option ${index + 1}`}
                className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              {options.length > POLL_LIMITS.MIN_OPTIONS && (
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  aria-label={`Remove option ${index + 1}`}
                  className="px-1 text-lg text-gray-400 hover:text-red-600"
                >
                  &#10005;
                </button>
              )}
            </div>
          ))}

          {duplicate && (
            <p className="mt-1 text-xs font-medium text-red-600">
              Two options are the same. Voters could not tell them apart.
            </p>
          )}
        </div>

        {/* anonymity */}
        <div className="border-b border-gray-200 px-5 py-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-blue-600"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-700">
                Keep the identity of the voters anonymous
              </span>
              <span className="block text-xs text-gray-400">
                Only the counts are shown - to everyone, including you as the
                author. This cannot be changed after the poll is posted.
              </span>
            </span>
          </label>
        </div>

        {/* duration */}
        <div className="px-5 py-4">
          <label
            htmlFor="poll-duration"
            className="mb-2 block text-sm font-semibold text-gray-700"
          >
            Poll Duration (days) <span className="text-red-500">*</span>
          </label>
          <input
            id="poll-duration"
            type="number"
            min={POLL_LIMITS.MIN_DAYS}
            max={POLL_LIMITS.MAX_DAYS}
            value={durationDays}
            onChange={(event) => setDurationDays(event.target.value)}
            placeholder="E.g. 3"
            className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-400">
            Between {POLL_LIMITS.MIN_DAYS} and {POLL_LIMITS.MAX_DAYS} days. The
            server turns this into the closing time and decides when the poll is
            closed.
          </p>
        </div>
      </div>

      <AudienceDialog
        open={audienceOpen}
        selectedId={audienceId}
        onClose={() => setAudienceOpen(false)}
        onConfirm={(id) => {
          setAudienceId(id);
          setAudienceOpen(false);
        }}
      />
    </div>
  );
};

export default CreatePoll;
